import { isSuboptimalBrowser } from '../base/environment/environment';
import { translateToHTML } from '../base/i18n/functions';
import { showWarningNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';

import { toState } from '../base/redux/functions';
import { areThereNotifications } from '../notifications/functions';
import { getOverlayToRender } from '../overlay/functions';
import { CONFERENCE_INFO } from './constants';

/**
 * Tells whether or not the notifications should be displayed within
 * the conference feature based on the current Redux state.
 *
 * @param {Object|Function} stateful - The redux store state.
 * @returns {boolean}
 */
export function shouldDisplayNotifications(stateful) {
  const state = toState(stateful);
  const isAnyOverlayVisible = Boolean(getOverlayToRender(state));

  return areThereNotifications(state) && !isAnyOverlayVisible;
}

/**
 * Retrieves the conference info labels based on config values and defaults.
 *
 * @param {Object} state - The redux state.
 * @returns {Object} The conferenceInfo object.
 */
export const getConferenceInfo = (state) => {
  const { conferenceInfo } = state['features/base/config'];

  if (conferenceInfo) {
    return {
      alwaysVisible:
        conferenceInfo.alwaysVisible ?? CONFERENCE_INFO.alwaysVisible,
      autoHide: conferenceInfo.autoHide ?? CONFERENCE_INFO.autoHide
    };
  }

  return CONFERENCE_INFO;
};

/**
 * Whether or not there are always on labels.
 *
 * @returns {boolean}
 */
export function isAlwaysOnTitleBarEmpty() {
  const bar = document.querySelector('#alwaysVisible>div');

  return bar?.childNodes.length === 0;
}

/**
 * Shows the suboptimal experience notification if needed.
 *
 * @param {Function} dispatch - The dispatch method.
 * @param {Function} t - The translation function.
 * @returns {void}
 */
export function maybeShowSuboptimalExperienceNotification(dispatch, t) {
  if (isSuboptimalBrowser()) {
    dispatch(
      showWarningNotification(
        {
          titleKey: 'notify.suboptimalExperienceTitle',
          description: translateToHTML(t, 'notify.suboptimalBrowserWarning', {
            recommendedBrowserPageLink: `${window.location.origin}/static/recommendedBrowsers.html`
          })
        },
        NOTIFICATION_TIMEOUT_TYPE.LONG
      )
    );
  }
}

/**
 * Appends a suffix to the display name.
 *
 * @param {string} displayName - The display name.
 * @param {string} suffix - Suffix that will be appended.
 * @returns {string} The formatted display name.
 */
export function appendSuffix(displayName, suffix = '') {
  return `${displayName || suffix}${
    displayName && suffix && displayName !== suffix ? ` (${suffix})` : ''
  }`;
}
